import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import Seo from "@/components/Seo";
import { ArrowLeft, Phone, Fuel, Weight, Layers, Circle, Truck, Calendar, Clock, CheckCircle, Send } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE = "514-973-4754";

export default function ForkliftDetailPage() {
  const { id } = useParams();
  const { t, lang } = useLanguage();
  const [forklift, setForklift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", contact: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(0);

  useEffect(() => {
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API}/forklifts/${id}`);
        setForklift(data);
      } catch {
        setForklift(null);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id]);

  if (loading) {
    return (
      <div className="pt-24 pb-16 min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!forklift) {
    return (
      <div className="pt-24 pb-16 min-h-screen bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center py-20">
          <Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2">
            {lang === "en" ? "Forklift not found" : "Chariot introuvable"}
          </h1>
          <Link to="/inventory" className="inline-flex items-center gap-2 text-sm font-medium text-yellow-600 hover:text-yellow-700 mt-4">
            <ArrowLeft className="w-4 h-4" /> {lang === "en" ? "Back to inventory" : "Retour à l'inventaire"}
          </Link>
        </div>
      </div>
    );
  }

  const desc = lang === "en" ? forklift.description_en : forklift.description_fr;
  const engineLabel = { propane: "Propane", diesel: "Diesel", electric: lang === "en" ? "Electric" : "Électrique" };
  const tireLabel = { pneumatic: lang === "en" ? "Pneumatic" : "Pneumatique", cushion: lang === "en" ? "Cushion" : "Coussin" };
  const conditionLabel = forklift.condition === "new" ? (lang === "en" ? "New" : "Neuf") : (lang === "en" ? "Used" : "Usagé");

  const resolveUrl = (url) => url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
  const allImages = [forklift.image_url, ...(forklift.images || [])].filter(Boolean).map(resolveUrl);
  const mainImg = allImages[Math.min(selectedIdx, allImages.length - 1)] || null;

  const specs = [
    { icon: Weight, label: lang === "en" ? "Capacity" : "Capacité", value: forklift.capacity_lbs ? `${forklift.capacity_lbs.toLocaleString()} lbs` : "—" },
    { icon: Fuel, label: lang === "en" ? "Engine" : "Moteur", value: engineLabel[forklift.engine_type] || forklift.engine_type || "—" },
    { icon: Layers, label: lang === "en" ? "Mast" : "Mât", value: forklift.mast_stages ? `${forklift.mast_stages} ${lang === "en" ? "stages" : "stages"}` : "—" },
    { icon: Circle, label: lang === "en" ? "Tires" : "Pneus", value: tireLabel[forklift.tire_type] || forklift.tire_type || "—" },
    { icon: Truck, label: lang === "en" ? "Drive" : "Traction", value: forklift.drive ? forklift.drive.toUpperCase() : "—" },
    { icon: Calendar, label: lang === "en" ? "Year" : "Année", value: forklift.year || "—" },
    { icon: Clock, label: lang === "en" ? "Hours" : "Heures", value: forklift.hours_used != null ? forklift.hours_used.toLocaleString() : "—" },
  ];

  return (
    <div data-testid="forklift-detail-page" className="pt-24 pb-16 min-h-screen bg-white">
      <Seo
        title={`${forklift.brand || ""} ${forklift.model || ""}${forklift.year ? ` (${forklift.year})` : ""} — ${conditionLabel} | Fork Finder`}
        description={(desc && desc.slice(0, 160)) || (lang === "en"
          ? `${conditionLabel} ${forklift.brand || ""} ${forklift.model || ""} forklift available in Quebec. ${forklift.capacity_lbs ? forklift.capacity_lbs.toLocaleString() + " lbs capacity. " : ""}Contact Fork Finder for availability.`
          : `Chariot élévateur ${conditionLabel.toLowerCase()} ${forklift.brand || ""} ${forklift.model || ""} disponible au Québec. ${forklift.capacity_lbs ? forklift.capacity_lbs.toLocaleString() + " lbs de capacité. " : ""}Contactez Fork Finder pour la disponibilité.`)}
        canonicalPath={`/inventory/${forklift.id}`}
        lang={lang}
        image={mainImg || undefined}
        type="product"
        alternates={{ fr: `/inventory/${forklift.id}`, en: `/inventory/${forklift.id}?lang=en` }}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: `${forklift.brand || ""} ${forklift.model || ""}`.trim(),
          description: desc || undefined,
          image: allImages.length ? allImages : undefined,
          brand: forklift.brand ? { "@type": "Brand", name: forklift.brand } : undefined,
          sku: forklift.id,
          category: "Forklift / Material Handling Equipment",
          itemCondition: forklift.condition === "new"
            ? "https://schema.org/NewCondition"
            : "https://schema.org/UsedCondition",
          ...(forklift.price && forklift.price > 0
            ? {
                offers: {
                  "@type": "Offer",
                  url: typeof window !== "undefined" ? window.location.href : undefined,
                  priceCurrency: "CAD",
                  price: forklift.price,
                  availability: "https://schema.org/InStock",
                  seller: { "@type": "Organization", name: "Fork Finder" },
                },
              }
            : {}),
          additionalProperty: [
            forklift.capacity_lbs && { "@type": "PropertyValue", name: "Capacity", value: `${forklift.capacity_lbs} lbs` },
            forklift.engine_type && { "@type": "PropertyValue", name: "Engine", value: forklift.engine_type },
            forklift.mast_stages && { "@type": "PropertyValue", name: "Mast stages", value: forklift.mast_stages },
            forklift.tire_type && { "@type": "PropertyValue", name: "Tire type", value: forklift.tire_type },
            forklift.drive && { "@type": "PropertyValue", name: "Drive", value: forklift.drive },
            forklift.year && { "@type": "PropertyValue", name: "Year", value: forklift.year },
            forklift.hours_used != null && { "@type": "PropertyValue", name: "Hours", value: forklift.hours_used },
          ].filter(Boolean),
        }}
      />
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        {/* Back link */}
        <Link
          to="/inventory"
          data-testid="back-to-inventory"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> {lang === "en" ? "Back to inventory" : "Retour à l'inventaire"}
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {/* Image gallery */}
          <div>
            <div className="aspect-[4/3] bg-gray-100 border border-gray-200 rounded-sm overflow-hidden">
              {mainImg ? (
                <img src={mainImg} alt={`${forklift.brand} ${forklift.model}`} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Truck className="w-24 h-24 text-gray-300" />
                </div>
              )}
            </div>
            {allImages.length > 1 && (
              <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
                {allImages.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedIdx(i)}
                    className={`w-16 h-12 rounded-sm overflow-hidden flex-shrink-0 border-2 transition-all ${i === selectedIdx ? "border-yellow-400" : "border-transparent opacity-60 hover:opacity-100"}`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <Badge className={forklift.condition === "new"
                  ? "bg-yellow-400 text-gray-900 border-none hover:bg-yellow-400 mb-2"
                  : "bg-gray-100 text-gray-700 border-none hover:bg-gray-100 mb-2"
                }>
                  {conditionLabel}
                </Badge>
                <h1 className="font-heading text-3xl sm:text-4xl font-black text-gray-900 tracking-tight">
                  {forklift.brand} {forklift.model}
                </h1>
                {forklift.year && (
                  <p className="text-sm text-gray-500 mt-1">{forklift.year}</p>
                )}
              </div>
            </div>

            {forklift.price > 0 && (
              <p className="font-mono text-3xl font-bold text-gray-900 mb-6">
                ${forklift.price.toLocaleString()}
              </p>
            )}

            {desc && (
              <p className="text-base text-gray-700 leading-relaxed mb-6">{desc}</p>
            )}

            {/* Specs grid */}
            <div className="grid grid-cols-2 gap-3 mb-8">
              {specs.map((spec, i) => (
                <div key={i} className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-sm px-4 py-3">
                  <spec.icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500">{spec.label}</p>
                    <p className="text-sm font-semibold text-gray-900 font-mono">{spec.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Contact CTAs */}
            <div className="border-t border-gray-200 pt-6">
              <p className="text-sm font-semibold text-gray-700 mb-1">
                {lang === "en" ? "Interested in this forklift?" : "Intéressé par ce chariot?"}
              </p>
              <p className="text-xs text-gray-500 mb-4">
                {lang === "en"
                  ? "Leave your info and we'll confirm availability right away."
                  : "Laissez vos coordonnées et on confirme la disponibilité rapidement."}
              </p>

              {submitted ? (
                <div data-testid="detail-form-success" className="bg-green-50 border border-green-200 rounded-sm p-5 text-center">
                  <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="font-heading font-bold text-gray-900">
                    {lang === "en" ? "Request sent!" : "Demande envoyée!"}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    {lang === "en"
                      ? "We'll get back to you shortly about this machine."
                      : "On vous revient sous peu au sujet de cette machine."}
                  </p>
                  <a
                    href={`tel:${PHONE.replace(/-/g, "")}`}
                    className="inline-flex items-center gap-2 text-sm font-bold text-yellow-600 hover:text-yellow-700 mt-3"
                  >
                    <Phone className="w-4 h-4" /> {lang === "en" ? "Need it faster? Call" : "Besoin plus vite? Appelez"} {PHONE}
                  </a>
                </div>
              ) : (
                <form
                  data-testid="detail-interest-form"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!form.name) {
                      toast.error(lang === "en" ? "Please fill in your name." : "Veuillez remplir votre nom.");
                      return;
                    }
                    setSubmitting(true);
                    try {
                      const isEmail = form.contact.includes("@");
                      const machineRef = `${forklift.brand} ${forklift.model}${forklift.year ? ` (${forklift.year})` : ""}`;
                      await axios.post(`${API}/customer-requests`, {
                        name: form.name,
                        email: isEmail ? form.contact : "lead@forkfinder.ca",
                        phone: isEmail ? "" : form.contact,
                        condition: forklift.condition || null,
                        engine_type: forklift.engine_type || null,
                        mast_stages: forklift.mast_stages || null,
                        tire_type: forklift.tire_type || null,
                        drive: forklift.drive || null,
                        capacity_min: forklift.capacity_lbs ? forklift.capacity_lbs - 500 : null,
                        capacity_max: forklift.capacity_lbs ? forklift.capacity_lbs + 500 : null,
                        notes: `[INTERESTED IN: ${machineRef} — $${forklift.price?.toLocaleString() || "N/A"}] Contact: ${form.contact}. ${form.message}`,
                      });
                      setSubmitted(true);
                    } catch {
                      toast.error(lang === "en" ? "Error sending request. Try calling us." : "Erreur. Essayez de nous appeler.");
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                  className="space-y-3"
                >
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">
                      {lang === "en" ? "Your Name" : "Votre nom"} *
                    </label>
                    <Input
                      data-testid="detail-input-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="bg-white"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">
                      {lang === "en" ? "Phone or Email" : "Téléphone ou courriel"}
                    </label>
                    <Input
                      data-testid="detail-input-contact"
                      value={form.contact}
                      onChange={(e) => setForm({ ...form, contact: e.target.value })}
                      placeholder={lang === "en" ? "514-555-1234 or you@email.com" : "514-555-1234 ou vous@courriel.com"}
                      className="bg-white"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600 mb-1 block">
                      {lang === "en" ? "Message (optional)" : "Message (optionnel)"}
                    </label>
                    <Input
                      data-testid="detail-input-message"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      placeholder={lang === "en" ? "Any questions about this machine?" : "Des questions sur cette machine?"}
                      className="bg-white"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="detail-submit-btn"
                    className="w-full inline-flex items-center justify-center gap-2 bg-yellow-400 text-gray-900 font-bold hover:bg-yellow-500 transition-colors px-6 py-3 rounded-sm disabled:opacity-50"
                  >
                    <Send className="w-4 h-4" />
                    {submitting ? "..." : (lang === "en" ? "Check Availability" : "Vérifier la disponibilité")}
                  </button>
                </form>
              )}

              <a
                href={`tel:${PHONE.replace(/-/g, "")}`}
                data-testid="detail-call-btn"
                className="w-full inline-flex items-center justify-center gap-2 bg-gray-100 text-gray-900 font-medium hover:bg-gray-200 transition-colors px-6 py-3 rounded-sm border border-gray-200 mt-3"
              >
                <Phone className="w-4 h-4" /> {lang === "en" ? "Or call us" : "Ou appelez-nous"}: {PHONE}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
